import {useEffect, useState} from 'react';
import {
    uploadImages,
    removeBackground,
    resizeImage,
    fileToBase64,
    convertImageUrlToBase64
} from '../api/helpers/imagesHelpers';
import {LoadingButton} from "./LoadingButton";

interface ImageUploaderProps {
    fullSize?: number;
    thumbSize?: number;
    imageSize?: number;
    onOriginalChange?: (original: File | null) => void;
    onMainImgChange?: (mainImg: string | null) => void;
    onThumbImgChange?: (thumbImg: string | null) => void;
    onImageRemoved?: () => void;
    originalImageUrl?: string | null;
    thumbUrl?: string | null;
    crop?: boolean;
}

export default function ImageUploader({
                                          fullSize = 1200,
                                          thumbSize = 400,
                                          imageSize = 200,
                                          onOriginalChange,
                                          onMainImgChange,
                                          onThumbImgChange,
                                          onImageRemoved,
                                          originalImageUrl,
                                          thumbUrl,
                                          crop = true
                                      }: ImageUploaderProps) {
    const [original, setOriginal] = useState<File | null>(null);
    const [mainImg, setMainImg] = useState<string | null>(null);
    const [thumbImg, setThumbImg] = useState<string | null>(null);
    const [bgRemovedImg, setBgRemovedImg] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);


    useEffect(() => {
        (async () => {
            if (originalImageUrl) {
                try {
                    const base64 = await convertImageUrlToBase64(originalImageUrl);
                    setMainImg(base64 || originalImageUrl);
                    setThumbImg(base64 || originalImageUrl);
           
                } catch (error) {
                    console.error('Error loading image as base64:', error);
                    setMainImg(originalImageUrl); // fallback
                }
            }
            if (thumbUrl) {
                try {
                    const base64 = await convertImageUrlToBase64(thumbUrl);
                    setThumbImg(base64 || thumbUrl);
                } catch (error) {
                    console.error('Error loading thumb as base64:', error);
                    setThumbImg(thumbUrl); // fallback
                }
            }
        })();
    }, [originalImageUrl, thumbUrl]);

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files?.length) return;
        const file = e.target.files[0];
        setOriginal(file);

        // Previsualizar
        const base64 = await fileToBase64(file);

        // Redimensionar/crop
        const main = await resizeImage(base64, fullSize, fullSize);
        setMainImg(main);

        const thumb = await resizeImage(base64, thumbSize, thumbSize);
        setThumbImg(thumb);
        setBgRemovedImg(null);

        if (onOriginalChange) {
            onOriginalChange(file);
        }
        if (onMainImgChange) {
            onMainImgChange(main);
        }
        if (onThumbImgChange) {
            onThumbImgChange(thumb);
        }

    };

    const handleRemoveBg = async () => {
        if (!mainImg) return;
        setLoading(true);
        try {
            const imgNoBg = await removeBackground(mainImg, crop);
            setBgRemovedImg(imgNoBg);
            
            const thumb = await resizeImage(imgNoBg, thumbSize, thumbSize);
            setThumbImg(thumb);

            if (onMainImgChange) {
                onMainImgChange(imgNoBg);
            }
            if (onThumbImgChange) {
                onThumbImgChange(thumb);
            }

        } catch (error) {
            console.error(error);
            alert('Error al quitar el fondo');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (!mainImg) return;
        const link = document.createElement('a');
        link.href = mainImg;
        link.download = 'image.png';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleRemoveImage = () => {
        setOriginal(null);
        setMainImg(null);
        setThumbImg(null);
        setBgRemovedImg(null);
        if (onOriginalChange) onOriginalChange(null);
        if (onMainImgChange) onMainImgChange(null);
        if (onThumbImgChange) onThumbImgChange(null);
        if (onImageRemoved) onImageRemoved();
    };

    return (
        <div className="d-flex flex-column align-items-center">

            {!mainImg && !thumbImg && (
                <div className="noImageSelected" style={{width: imageSize, height: imageSize}}>
                    <p className="mb-1 text-center">Selecciona una imagen</p>
                    <p className="small">Tamaño sugerido:</p>
                    <p className="small">{fullSize} x {fullSize} px</p>
                </div>
            )}


            {thumbImg && (
                <img src={thumbImg} className="imageUploaderImg" alt="thumb"
                     style={{width: imageSize, height: imageSize, marginBottom: '2rem'}}/>
            )}

            <div className="d-flex flex-column gap-2">
                <label className="btn btn-success w-100">
                    Seleccionar imagen
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileChange}
                        disabled={loading}
                        style={{display: 'none'}}
                    />
                </label>
                {thumbImg && (
                    <>
                        <LoadingButton
                            type="button"
                            isLoading={loading}
                            onClick={handleRemoveBg}
                            className="btn btn-lg btn-primary w-100">Quitar fondo</LoadingButton>
                        <button type="button" className="btn btn-lg btn-secondary w-100"
                                onClick={handleDownload}>Descargar imagen
                        </button>
                        <button type="button" className="btn btn-lg btn-danger w-100" onClick={handleRemoveImage}>
                            Eliminar imagen
                        </button>
                    </>
                )}
            </div>

        </div>
    );
}
export {};